import type {
  ConvertibleIssue,
  ConvertibleScanParams,
  ConvertibleScanResult,
  Filing,
  FilingSearchParams,
  FlaggedSecurity,
  FormType,
  IdeaSymbolContext,
  InvestmentIdea,
  MarkerGroup,
  MarkerInfluence,
  MarkerLabParams,
  MarkerLabResult,
  McConfig,
  McPosition,
  McResult,
  McSensitivity,
  OfferingTerm,
  OpusAnalysisRequest,
  OpusAnalysisResponse,
  PcaComponentSummary,
  ScreenerCriteria,
  ScreenerResult,
  Sector,
  SectorFactorLoadings,
  SizeBucket,
  SizePlacement,
  SymbolSignal,
  VolatilityProfile,
} from '../types/research'
import { MARKERS, SECTORS } from '../types/research'
import { apiGet, apiPost, apiPut, apiDelete } from './api-client'
import {
  bsDelta,
  bsPrice,
  correlation,
  distributionStats,
  gaussian,
  hashSeed,
  histogram,
  impliedVol,
  mean,
  mulberry32,
  normalPdf,
  olsRegression,
  pca,
  standardize,
} from './quant'

const USE_MOCK = !import.meta.env.VITE_FINANCE_API_URL

let _usingMockData = USE_MOCK
export function isResearchUsingMockData(): boolean {
  return _usingMockData
}

// ─── Mock universe ───────────────────────────────────────────
// Deterministic per-symbol pseudo-randomness so the screener is stable across
// renders and navigation (no flicker between visits).

interface UniverseEntry {
  symbol: string
  name: string
  sector: Sector
  price: number
  marketCap: number // $B
  baseVol: number // sector-typical annualized vol
  hasConvert?: boolean
  hasPreferred?: boolean
}

const UNIVERSE: UniverseEntry[] = [
  // Technology
  { symbol: 'AAPL', name: 'Apple Inc.', sector: 'Technology', price: 178.72, marketCap: 2780, baseVol: 27 },
  { symbol: 'MSFT', name: 'Microsoft Corporation', sector: 'Technology', price: 415.2, marketCap: 3090, baseVol: 24 },
  { symbol: 'NVDA', name: 'NVIDIA Corporation', sector: 'Technology', price: 131.4, marketCap: 3230, baseVol: 44 },
  { symbol: 'AVGO', name: 'Broadcom Inc.', sector: 'Technology', price: 168.9, marketCap: 790, baseVol: 34 },
  { symbol: 'MU', name: 'Micron Technology', sector: 'Technology', price: 104.2, marketCap: 115, baseVol: 46, hasConvert: true },
  { symbol: 'PLTR', name: 'Palantir Technologies', sector: 'Technology', price: 28.4, marketCap: 63, baseVol: 55, hasConvert: true },
  // Communication Services
  { symbol: 'GOOGL', name: 'Alphabet Inc.', sector: 'Communication Services', price: 155.3, marketCap: 1930, baseVol: 28 },
  { symbol: 'META', name: 'Meta Platforms Inc.', sector: 'Communication Services', price: 505.1, marketCap: 1280, baseVol: 33 },
  { symbol: 'NFLX', name: 'Netflix Inc.', sector: 'Communication Services', price: 640.5, marketCap: 275, baseVol: 36 },
  { symbol: 'DIS', name: 'The Walt Disney Company', sector: 'Communication Services', price: 96.7, marketCap: 176, baseVol: 29 },
  // Consumer Discretionary
  { symbol: 'AMZN', name: 'Amazon.com Inc.', sector: 'Consumer Discretionary', price: 186.4, marketCap: 1940, baseVol: 31 },
  { symbol: 'TSLA', name: 'Tesla Inc.', sector: 'Consumer Discretionary', price: 248.5, marketCap: 792, baseVol: 52, hasConvert: true },
  { symbol: 'HD', name: 'The Home Depot', sector: 'Consumer Discretionary', price: 342.1, marketCap: 340, baseVol: 22 },
  { symbol: 'NKE', name: 'Nike Inc.', sector: 'Consumer Discretionary', price: 78.9, marketCap: 118, baseVol: 30 },
  { symbol: 'CCL', name: 'Carnival Corporation', sector: 'Consumer Discretionary', price: 16.8, marketCap: 21, baseVol: 48, hasConvert: true },
  // Consumer Staples
  { symbol: 'PG', name: 'Procter & Gamble', sector: 'Consumer Staples', price: 166.2, marketCap: 392, baseVol: 16 },
  { symbol: 'KO', name: 'The Coca-Cola Company', sector: 'Consumer Staples', price: 62.8, marketCap: 271, baseVol: 15 },
  { symbol: 'COST', name: 'Costco Wholesale', sector: 'Consumer Staples', price: 845.3, marketCap: 375, baseVol: 21 },
  { symbol: 'WMT', name: 'Walmart Inc.', sector: 'Consumer Staples', price: 68.4, marketCap: 550, baseVol: 18 },
  // Energy
  { symbol: 'XOM', name: 'Exxon Mobil', sector: 'Energy', price: 113.5, marketCap: 450, baseVol: 24 },
  { symbol: 'CVX', name: 'Chevron Corporation', sector: 'Energy', price: 152.3, marketCap: 280, baseVol: 23 },
  { symbol: 'OXY', name: 'Occidental Petroleum', sector: 'Energy', price: 58.6, marketCap: 52, baseVol: 32, hasPreferred: true },
  { symbol: 'SLB', name: 'Schlumberger', sector: 'Energy', price: 43.2, marketCap: 61, baseVol: 33 },
  // Financials
  { symbol: 'JPM', name: 'JPMorgan Chase & Co.', sector: 'Financials', price: 198.5, marketCap: 570, baseVol: 21, hasPreferred: true },
  { symbol: 'BAC', name: 'Bank of America', sector: 'Financials', price: 39.7, marketCap: 310, baseVol: 25, hasPreferred: true },
  { symbol: 'GS', name: 'Goldman Sachs Group', sector: 'Financials', price: 462.8, marketCap: 148, baseVol: 26, hasPreferred: true },
  { symbol: 'SCHW', name: 'Charles Schwab', sector: 'Financials', price: 72.4, marketCap: 128, baseVol: 28, hasPreferred: true },
  { symbol: 'V', name: 'Visa Inc.', sector: 'Financials', price: 275.6, marketCap: 560, baseVol: 19 },
  // Healthcare
  { symbol: 'JNJ', name: 'Johnson & Johnson', sector: 'Healthcare', price: 147.9, marketCap: 356, baseVol: 15 },
  { symbol: 'LLY', name: 'Eli Lilly and Company', sector: 'Healthcare', price: 812.4, marketCap: 772, baseVol: 32 },
  { symbol: 'PFE', name: 'Pfizer Inc.', sector: 'Healthcare', price: 27.3, marketCap: 155, baseVol: 24 },
  { symbol: 'MRNA', name: 'Moderna Inc.', sector: 'Healthcare', price: 118.2, marketCap: 45, baseVol: 58, hasConvert: true },
  // Industrials
  { symbol: 'BA', name: 'The Boeing Company', sector: 'Industrials', price: 178.3, marketCap: 110, baseVol: 38, hasConvert: true },
  { symbol: 'CAT', name: 'Caterpillar Inc.', sector: 'Industrials', price: 336.5, marketCap: 163, baseVol: 25 },
  { symbol: 'UPS', name: 'United Parcel Service', sector: 'Industrials', price: 132.8, marketCap: 113, baseVol: 24 },
  { symbol: 'GE', name: 'GE Aerospace', sector: 'Industrials', price: 162.1, marketCap: 176, baseVol: 27 },
  // Materials
  { symbol: 'LIN', name: 'Linde plc', sector: 'Materials', price: 441.2, marketCap: 212, baseVol: 19 },
  { symbol: 'FCX', name: 'Freeport-McMoRan', sector: 'Materials', price: 44.6, marketCap: 64, baseVol: 37 },
  { symbol: 'NUE', name: 'Nucor Corporation', sector: 'Materials', price: 158.7, marketCap: 38, baseVol: 31 },
  // Real Estate
  { symbol: 'PLD', name: 'Prologis Inc.', sector: 'Real Estate', price: 112.4, marketCap: 104, baseVol: 25, hasPreferred: true },
  { symbol: 'AMT', name: 'American Tower', sector: 'Real Estate', price: 198.6, marketCap: 93, baseVol: 23 },
  { symbol: 'O', name: 'Realty Income', sector: 'Real Estate', price: 55.8, marketCap: 49, baseVol: 20, hasPreferred: true },
  // Utilities
  { symbol: 'NEE', name: 'NextEra Energy', sector: 'Utilities', price: 74.2, marketCap: 153, baseVol: 22, hasConvert: true },
  { symbol: 'DUK', name: 'Duke Energy', sector: 'Utilities', price: 108.9, marketCap: 84, baseVol: 16, hasPreferred: true },
  { symbol: 'SO', name: 'The Southern Company', sector: 'Utilities', price: 84.3, marketCap: 92, baseVol: 16, hasConvert: true },
]

/** Deterministic 0–1 hash per (symbol, salt) so mock values are stable. */
function seeded(symbol: string, salt: number): number {
  let h = 2166136261 ^ salt
  for (let i = 0; i < symbol.length; i++) {
    h ^= symbol.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return ((h >>> 0) % 10000) / 10000
}

function isoDaysFromNow(days: number): string {
  const d = new Date(Date.now() + days * 86400000)
  return d.toISOString().slice(0, 10)
}

function mockProfile(e: UniverseEntry): VolatilityProfile {
  const realized = +(e.baseVol * (0.85 + seeded(e.symbol, 1) * 0.4)).toFixed(1)
  // Skew implied around realized: some names rich, some cheap
  const skew = 0.72 + seeded(e.symbol, 2) * 0.62 // 0.72 – 1.34
  const implied = +(realized * skew).toFixed(1)
  const implied7 = +(implied * (0.9 + seeded(e.symbol, 3) * 0.35)).toFixed(1)
  const daysToEarnings = 3 + Math.floor(seeded(e.symbol, 4) * 75)
  return {
    symbol: e.symbol,
    name: e.name,
    sector: e.sector,
    price: e.price,
    marketCap: e.marketCap * 1e9,
    realizedVol30d: realized,
    impliedVol30d: implied,
    impliedVol7d: implied7,
    volDivergence: +(implied - realized).toFixed(1),
    divergenceRatio: +(implied / realized).toFixed(2),
    ivPercentile52w: Math.round(seeded(e.symbol, 5) * 100),
    nextEarningsDate: isoDaysFromNow(daysToEarnings),
    daysUntilEarnings: daysToEarnings,
  }
}

function edgarCompanyUrl(symbol: string, type?: string): string {
  const base = `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${encodeURIComponent(symbol)}`
  return type ? `${base}&type=${encodeURIComponent(type)}&dateb=&owner=include&count=10` : `${base}&owner=include&count=10`
}

function mockFilings(e: UniverseEntry): Filing[] {
  const filings: Filing[] = []
  const push = (formType: FormType, daysAgo: number, description: string) => {
    filings.push({
      accessionNumber: `${e.symbol}-${formType}-${daysAgo}`,
      symbol: e.symbol,
      company: e.name,
      sector: e.sector,
      formType,
      filedAt: isoDaysFromNow(-daysAgo),
      periodEnd: formType === '10-Q' || formType === '10-K' ? isoDaysFromNow(-daysAgo - 40) : null,
      description,
      edgarUrl: edgarCompanyUrl(e.symbol, formType),
      documentUrl: null,
    })
  }
  push('8-K', 2 + Math.floor(seeded(e.symbol, 6) * 20), 'Current report — material events and press releases')
  push('10-Q', 15 + Math.floor(seeded(e.symbol, 7) * 50), 'Quarterly report')
  push('10-K', 90 + Math.floor(seeded(e.symbol, 8) * 120), 'Annual report')
  if (e.hasConvert)
    push('424B5', 20 + Math.floor(seeded(e.symbol, 9) * 90), 'Prospectus supplement — convertible senior notes offering')
  if (e.hasPreferred)
    push('S-3', 30 + Math.floor(seeded(e.symbol, 10) * 120), 'Shelf registration — preferred stock and debt securities')
  return filings.sort((a, b) => b.filedAt.localeCompare(a.filedAt))
}

function mockOfferingTerm(e: UniverseEntry): OfferingTerm {
  const filing = mockFilings(e).find((f) => f.formType === '424B5' || f.formType === 'S-3') || null
  if (e.hasConvert) {
    const coupon = (0.25 + seeded(e.symbol, 11) * 3).toFixed(2)
    const premium = Math.round(27.5 + seeded(e.symbol, 12) * 20)
    const convPrice = +(e.price * (1 + premium / 100)).toFixed(2)
    return {
      symbol: e.symbol,
      company: e.name,
      instrument: 'Convertible Senior Notes',
      summary: `${e.name} has convertible senior notes outstanding with a ${coupon}% coupon and an initial conversion premium of ~${premium}% (conversion price ≈ $${convPrice}). Conversion is subject to customary anti-dilution adjustments and a make-whole table on fundamental changes.`,
      keyTerms: [
        { label: 'Coupon', value: `${coupon}%` },
        { label: 'Conversion price', value: `$${convPrice}` },
        { label: 'Conversion premium', value: `~${premium}%` },
        { label: 'Settlement', value: 'Cash, shares, or combination at issuer election' },
        { label: 'Make-whole', value: 'Fundamental-change make-whole table' },
      ],
      sourceFiling: filing,
    }
  }
  const rate = (4.0 + seeded(e.symbol, 13) * 3.5).toFixed(2)
  return {
    symbol: e.symbol,
    company: e.name,
    instrument: 'Perpetual Preferred Stock',
    summary: `${e.name} has non-cumulative perpetual preferred outstanding paying ${rate}%. The series is redeemable at the issuer's option after the first call date; no conversion into common except upon specified change-of-control events.`,
    keyTerms: [
      { label: 'Dividend rate', value: `${rate}% non-cumulative` },
      { label: 'Liquidation preference', value: '$25,000 per share ($25 depositary)' },
      { label: 'Call feature', value: 'Issuer-redeemable after first call date' },
      { label: 'Conversion', value: 'None, except change-of-control conversion terms' },
    ],
    sourceFiling: filing,
  }
}

function delay(ms: number) {
  return new Promise((r) => setTimeout(r, ms))
}

// ─── Screener ────────────────────────────────────────────────

function runMockScreen(criteria: ScreenerCriteria): ScreenerResult {
  let rows = UNIVERSE.map(mockProfile)

  if (criteria.sectors && criteria.sectors.length > 0) {
    const set = new Set(criteria.sectors)
    rows = rows.filter((r) => set.has(r.sector))
  }
  if (criteria.direction === 'rich') rows = rows.filter((r) => r.volDivergence > 0)
  if (criteria.direction === 'cheap') rows = rows.filter((r) => r.volDivergence < 0)
  if (criteria.minDivergence) rows = rows.filter((r) => Math.abs(r.volDivergence) >= criteria.minDivergence!)
  if (criteria.minMarketCap) rows = rows.filter((r) => r.marketCap >= criteria.minMarketCap!)

  const sortBy = criteria.sortBy || 'divergence'
  rows.sort((a, b) => {
    switch (sortBy) {
      case 'ratio':
        return Math.abs(b.divergenceRatio - 1) - Math.abs(a.divergenceRatio - 1)
      case 'impliedVol':
        return b.impliedVol30d - a.impliedVol30d
      case 'marketCap':
        return b.marketCap - a.marketCap
      case 'earnings':
        return (a.daysUntilEarnings ?? 999) - (b.daysUntilEarnings ?? 999)
      default:
        return Math.abs(b.volDivergence) - Math.abs(a.volDivergence)
    }
  })

  let bySector: Record<string, VolatilityProfile[]> | null = null
  if (criteria.groupBySector) {
    bySector = {}
    for (const r of rows) {
      ;(bySector[r.sector] ||= []).push(r)
    }
  }

  if (criteria.limit && !criteria.groupBySector) rows = rows.slice(0, criteria.limit)
  if (criteria.limit && bySector) {
    for (const key of Object.keys(bySector)) bySector[key] = bySector[key].slice(0, criteria.limit)
  }

  return { asOf: new Date().toISOString(), criteria, results: rows, bySector }
}

export async function screenVolatility(criteria: ScreenerCriteria): Promise<ScreenerResult> {
  if (USE_MOCK) {
    _usingMockData = true
    await delay(500)
    return runMockScreen(criteria)
  }
  try {
    const result = await apiPost<ScreenerResult>('/api/finance/research/screener', criteria)
    _usingMockData = false
    return result
  } catch {
    console.warn('Screener API failed, falling back to mock data')
    _usingMockData = true
    return runMockScreen(criteria)
  }
}

// ─── Filings ─────────────────────────────────────────────────

function runMockFilingSearch(params: FilingSearchParams): Filing[] {
  let entries = UNIVERSE
  if (params.symbol) {
    const sym = params.symbol.toUpperCase()
    entries = entries.filter((e) => e.symbol === sym)
    // Unknown-but-requested symbol still gets generic filings so the UI works
    if (entries.length === 0) {
      entries = [{ symbol: sym, name: `${sym} Corp`, sector: 'Technology', price: 100, marketCap: 10, baseVol: 30 }]
    }
  }
  if (params.sector) entries = entries.filter((e) => e.sector === params.sector)
  let filings = entries.flatMap(mockFilings)
  if (params.formTypes && params.formTypes.length > 0) {
    const set = new Set(params.formTypes)
    filings = filings.filter((f) => set.has(f.formType))
  }
  filings.sort((a, b) => b.filedAt.localeCompare(a.filedAt))
  return filings.slice(0, params.limit ?? 60)
}

export async function searchFilings(params: FilingSearchParams): Promise<Filing[]> {
  if (USE_MOCK) {
    _usingMockData = true
    await delay(450)
    return runMockFilingSearch(params)
  }
  try {
    const qs = new URLSearchParams()
    if (params.symbol) qs.set('symbol', params.symbol)
    if (params.sector) qs.set('sector', params.sector)
    if (params.formTypes?.length) qs.set('forms', params.formTypes.join(','))
    if (params.limit) qs.set('limit', String(params.limit))
    const result = await apiGet<Filing[]>(`/api/finance/research/filings?${qs}`)
    _usingMockData = false
    return result
  } catch {
    console.warn('Filings API failed, falling back to mock data')
    _usingMockData = true
    return runMockFilingSearch(params)
  }
}

// ─── Investment ideas ────────────────────────────────────────
// Ideas persist to localStorage always (instant, offline-safe) and sync to the
// backend when it is configured, mirroring the mock-fallback pattern.

const IDEAS_KEY = 'nb-research-ideas'

function readLocalIdeas(): InvestmentIdea[] {
  try {
    const raw = localStorage.getItem(IDEAS_KEY)
    if (raw) return JSON.parse(raw) as InvestmentIdea[]
  } catch {
    // corrupted or unavailable storage — start fresh
  }
  return []
}

function writeLocalIdeas(ideas: InvestmentIdea[]) {
  try {
    localStorage.setItem(IDEAS_KEY, JSON.stringify(ideas))
  } catch {
    // non-fatal
  }
}

export async function listIdeas(): Promise<InvestmentIdea[]> {
  if (!USE_MOCK) {
    try {
      const remote = await apiGet<InvestmentIdea[]>('/api/finance/research/ideas')
      _usingMockData = false
      writeLocalIdeas(remote)
      return remote
    } catch {
      console.warn('Ideas API failed, using local storage')
      _usingMockData = true
    }
  }
  return readLocalIdeas()
}

export async function saveIdea(
  idea: Omit<InvestmentIdea, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }
): Promise<InvestmentIdea> {
  const now = new Date().toISOString()
  const ideas = readLocalIdeas()
  let saved: InvestmentIdea
  if (idea.id) {
    const existing = ideas.find((i) => i.id === idea.id)
    saved = {
      ...(existing ?? { id: idea.id, createdAt: now }),
      ...idea,
      id: idea.id,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    }
    writeLocalIdeas(ideas.map((i) => (i.id === saved.id ? saved : i)))
  } else {
    saved = { ...idea, id: `idea-${Date.now()}-${Math.floor(Math.random() * 1e5)}`, createdAt: now, updatedAt: now }
    writeLocalIdeas([saved, ...ideas])
  }
  if (!USE_MOCK) {
    apiPut<InvestmentIdea>(`/api/finance/research/ideas/${saved.id}`, saved).catch(() =>
      console.warn('Idea sync to API failed; kept locally')
    )
  }
  return saved
}

export async function deleteIdea(id: string): Promise<void> {
  writeLocalIdeas(readLocalIdeas().filter((i) => i.id !== id))
  if (!USE_MOCK) {
    apiDelete(`/api/finance/research/ideas/${id}`).catch(() =>
      console.warn('Idea delete sync to API failed; removed locally')
    )
  }
}

/** Price/earnings/filings context for a tracked symbol. */
export async function getIdeaSymbolContext(symbol: string): Promise<IdeaSymbolContext> {
  const sym = symbol.toUpperCase()
  if (!USE_MOCK) {
    try {
      const result = await apiGet<IdeaSymbolContext>(
        `/api/finance/research/ideas/context?symbol=${encodeURIComponent(sym)}`
      )
      _usingMockData = false
      return result
    } catch {
      _usingMockData = true
    }
  } else {
    await delay(250)
  }
  const entry = UNIVERSE.find((e) => e.symbol === sym)
  if (!entry) {
    return {
      symbol: sym,
      name: null,
      price: null,
      changePercent: null,
      nextEarningsDate: null,
      daysUntilEarnings: null,
      latestFilings: runMockFilingSearch({ symbol: sym, limit: 3 }),
    }
  }
  const profile = mockProfile(entry)
  return {
    symbol: sym,
    name: entry.name,
    price: entry.price,
    changePercent: +((seeded(sym, 14) - 0.5) * 4).toFixed(2),
    nextEarningsDate: profile.nextEarningsDate,
    daysUntilEarnings: profile.daysUntilEarnings,
    latestFilings: mockFilings(entry).slice(0, 3),
  }
}

// ─── Opus research assistant ─────────────────────────────────

function pickUniverse(req: OpusAnalysisRequest): UniverseEntry[] {
  let pool = UNIVERSE
  if (req.sector) pool = pool.filter((e) => e.sector === req.sector)
  if (req.symbols && req.symbols.length > 0) {
    const set = new Set(req.symbols.map((s) => s.toUpperCase()))
    pool = UNIVERSE.filter((e) => set.has(e.symbol))
  }
  return pool
}

function toFlagged(e: UniverseEntry, reason: string, score: number): FlaggedSecurity {
  const p = mockProfile(e)
  return {
    symbol: e.symbol,
    name: e.name,
    sector: e.sector,
    score,
    reason,
    volDivergence: p.volDivergence,
    nextEarningsDate: p.nextEarningsDate,
    relatedFilings: mockFilings(e).slice(0, 2),
  }
}

function mockOpusResponse(req: OpusAnalysisRequest): OpusAnalysisResponse {
  const base: OpusAnalysisResponse = {
    id: `analysis-${Date.now()}`,
    task: req.task,
    model: 'claude-opus-5 (demo output)',
    createdAt: new Date().toISOString(),
    summary: '',
    keyFindings: [],
    flagged: [],
    offerings: [],
    citations: [],
    isMock: true,
  }

  switch (req.task) {
    case 'analyze-filing': {
      const sym = (req.symbol || 'the issuer').toUpperCase()
      const entry = UNIVERSE.find((e) => e.symbol === sym)
      const name = entry?.name ?? sym
      base.summary =
        `Demo analysis of the latest filing for ${name}.\n\n` +
        `The filing describes ordinary-course operating results with two capital-structure items worth attention: outstanding senior notes maturing inside 24 months, and an untapped shelf registration that would allow an opportunistic equity or convert issuance if implied volatility stays elevated.\n\n` +
        (req.prompt ? `On your question — "${req.prompt}" — the demo backend cannot read the actual document; connect the live research API for grounded answers with citations to the filing text.` : `Connect the live research API for grounded answers with citations to the filing text.`)
      base.keyFindings = [
        'Liquidity: revolver undrawn; cash covers near-term maturities.',
        'Capital structure: shelf registration on file — issuance optionality when options are rich.',
        'Risk factors: no new litigation or going-concern language versus the prior period.',
      ]
      if (entry) base.citations = [{ label: `${sym} filings on EDGAR`, url: edgarCompanyUrl(sym) }]
      break
    }
    case 'analyze-security': {
      const sym = (req.symbol || 'AAPL').toUpperCase()
      const entry = UNIVERSE.find((e) => e.symbol === sym)
      const p = entry ? mockProfile(entry) : null
      base.summary =
        `Demo securities work-up for ${entry?.name ?? sym}.` +
        (p
          ? `\n\n30-day implied vol is ${p.impliedVol30d}% against ${p.realizedVol30d}% realized — a divergence of ${p.volDivergence > 0 ? '+' : ''}${p.volDivergence} vol points (${p.volDivergence > 0 ? 'options rich' : 'options cheap'} vs. recent movement). Next earnings in ~${p.daysUntilEarnings} days, so the short-dated term structure carries an event premium.`
          : '\n\nSymbol not in the demo universe; the live API resolves any listed security.') +
        (req.includeOptionsChain ? '\n\nOptions chain: skew is put-biased at the front expiry; open interest concentrates near the at-the-money strikes.' : '') +
        (req.includeFundamentals ? '\n\nFundamentals: margins and leverage are within their 3-year band; nothing in the balance sheet contradicts the vol picture.' : '')
      base.keyFindings = p
        ? [
            `${p.volDivergence > 0 ? 'Implied above realized' : 'Implied below realized'} by ${Math.abs(p.volDivergence)} pts (ratio ${p.divergenceRatio}).`,
            `IV sits at the ${p.ivPercentile52w}th percentile of its 52-week range.`,
            `Earnings ${p.nextEarningsDate} — consider event risk when structuring.`,
          ]
        : ['Connect the live API for real quotes, chains, and fundamentals.']
      if (entry) base.citations = [{ label: `${sym} filings on EDGAR`, url: edgarCompanyUrl(sym) }]
      break
    }
    case 'scan-offerings': {
      const pool = pickUniverse(req).filter((e) => e.hasConvert || e.hasPreferred)
      base.offerings = pool.slice(0, 6).map(mockOfferingTerm)
      base.summary =
        `Demo scan of ${req.sector ? `${req.sector} sector` : req.symbols?.length ? 'your list' : 'the coverage universe'} for structured security offerings — convertible debt and preferred equity with conversion features.\n\n` +
        (base.offerings.length
          ? `Found ${base.offerings.length} issuers with relevant instruments. Convertible structures cluster in higher-vol names (issuers monetize rich implied vol through the embedded call); preferred structures cluster in balance-sheet-heavy sectors.`
          : 'No issuers with convertible or preferred structures matched the demo universe for this scope.')
      base.keyFindings = base.offerings.map(
        (o) => `${o.symbol}: ${o.instrument} — ${o.keyTerms[0]?.label} ${o.keyTerms[0]?.value}`
      )
      break
    }
    case 'flag-thesis': {
      const minDiv = req.minDivergence ?? 5
      const pool = pickUniverse(req)
        .map((e) => ({ e, p: mockProfile(e) }))
        .filter(({ p }) => Math.abs(p.volDivergence) >= minDiv)
        .sort((a, b) => Math.abs(b.p.volDivergence) - Math.abs(a.p.volDivergence))
      base.flagged = pool
        .slice(0, 8)
        .map(({ e, p }, i) =>
          toFlagged(
            e,
            `${Math.abs(p.volDivergence)} pt ${p.volDivergence > 0 ? 'rich' : 'cheap'} short-term vol divergence${req.prompt ? ' consistent with the stated thesis' : ''}; earnings in ${p.daysUntilEarnings} days.`,
            92 - i * 6
          )
        )
      base.summary =
        `Demo thesis screen${req.sector ? ` across ${req.sector}` : ''}${req.symbols?.length ? ` across ${req.symbols.length} names` : ''} with a ±${minDiv} vol-point short-term divergence threshold.\n\n` +
        (req.prompt ? `Thesis: "${req.prompt}"\n\n` : '') +
        (base.flagged.length
          ? `${base.flagged.length} securities flagged for review, ranked by divergence magnitude and earnings proximity. Each flag links the two most recent filings for verification.`
          : 'No securities met the divergence threshold in the demo universe — lower the threshold or widen the scope.')
      break
    }
    case 'match-position': {
      const type = (req.positionType || '').toLowerCase()
      const wantsConvert = /convert/.test(type)
      const wantsPreferred = /preferred/.test(type)
      const wantsShortBonds = /short[- ]?term|bond|note|fixed income|duration/.test(type)
      let pool = pickUniverse(req)
      let rationale: (e: UniverseEntry) => string
      if (wantsConvert) {
        pool = pool.filter((e) => e.hasConvert)
        rationale = (e) => `${e.name} has convertible senior notes outstanding — matches "${req.positionType}".`
      } else if (wantsPreferred) {
        pool = pool.filter((e) => e.hasPreferred)
        rationale = (e) => `${e.name} has preferred equity outstanding — matches "${req.positionType}".`
      } else if (wantsShortBonds) {
        pool = pool.filter((e) => e.hasConvert || e.hasPreferred || e.marketCap > 200)
        rationale = (e) =>
          `${e.name} has investment-grade near-dated paper in the demo dataset suitable for a short-duration allocation.`
      } else {
        rationale = (e) => `${e.name} matches the requested position profile in the demo dataset.`
      }
      base.flagged = pool.slice(0, 8).map((e, i) => toFlagged(e, rationale(e), 90 - i * 5))
      base.summary =
        `Demo search for securities matching position type: "${req.positionType || 'unspecified'}"${req.sector ? ` within ${req.sector}` : ''}.\n\n` +
        (base.flagged.length
          ? `${base.flagged.length} candidates returned. The live backend screens actual issue-level data (maturities, coupons, call schedules, conversion terms) pulled from filings and the options/fundamentals stack.`
          : 'No candidates in the demo universe — the live backend searches issue-level data across the full market.')
      break
    }
  }
  return base
}

export async function analyzeWithOpus(req: OpusAnalysisRequest): Promise<OpusAnalysisResponse> {
  if (USE_MOCK) {
    _usingMockData = true
    await delay(1400)
    return mockOpusResponse(req)
  }
  try {
    const result = await apiPost<OpusAnalysisResponse>('/api/finance/research/opus', req)
    _usingMockData = false
    return result
  } catch {
    console.warn('Opus research API failed, falling back to demo output')
    _usingMockData = true
    return mockOpusResponse(req)
  }
}

// ─── Marker Lab: unsupervised marker-influence analysis ──────
// The mock builds a deterministic weekly panel (104 weeks × universe × 13
// markers) with planted marker→forward-return structure, then runs the SAME
// pipeline the backend would run on real data: standardize → PCA on the
// marker correlation matrix (unsupervised latent factors) → per-marker
// forward-return correlations (influence ranking) → Fama–French OLS per
// sector. The learning is real; only the panel is synthetic.

const LAB_WEEKS = 104
const LAB_MAX_HORIZON = 12
const LAB_N = LAB_WEEKS + LAB_MAX_HORIZON
const LAB_WARMUP = 8

interface LabPanel {
  /** symbol → markers[markerIdx][week], standardized per series */
  markers: Map<string, number[][]>
  /** symbol → weekly return (decimal), index 0 unused */
  returns: Map<string, number[]>
  factors: { mkt: number[]; smb: number[]; hml: number[] }
}

function arSeries(rng: () => number, n: number, phi: number, scale: number): number[] {
  const out = new Array<number>(n)
  let prev = 0
  for (let t = 0; t < n; t++) {
    prev = phi * prev + gaussian(rng) * scale
    out[t] = prev
  }
  return out
}

/** Sector-specific multiplier/sign on a planted marker weight. */
function sectorWeightTilt(sector: Sector, markerId: string): number {
  const u = (hashSeed(`${sector}|${markerId}`) % 10000) / 10000
  let m = 0.6 + u * 0.9 // 0.6 – 1.5
  // A few economically-motivated sign conventions in the synthetic data:
  if (markerId === 'jobs-surprise' && (sector === 'Real Estate' || sector === 'Utilities')) m = -m
  if (markerId === 'ff-hml' && (sector === 'Technology' || sector === 'Communication Services')) m = -m
  if (markerId === 'usd-fx' && sector === 'Financials') m = -0.4 * m
  return m
}

const PLANTED_WEEKLY_WEIGHTS: Record<string, number> = {
  'iv-divergence': -0.0016,
  'options-skew': -0.0009,
  'options-volume': 0.0005,
  'x-mentions': 0.0013,
  'x-sentiment': 0.0019,
  'news-tone': 0.0011,
  'earnings-surprise': 0.0026,
  'fed-rate': -0.0011,
  'fed-shock': -0.0021,
  'jobs-surprise': 0.0007,
  'usd-fx': -0.0009,
  'ff-smb': 0.0006,
  'ff-hml': 0.0008,
}

let _labPanel: LabPanel | null = null

function buildLabPanel(): LabPanel {
  if (_labPanel) return _labPanel

  // Shared macro/factor latents (identical across symbols).
  const mkRng = (name: string) => mulberry32(hashSeed(`latent:${name}`))
  const mkt: number[] = []
  {
    const rng = mkRng('mkt')
    for (let t = 0; t < LAB_N; t++) mkt.push(0.0015 + gaussian(rng) * 0.022)
  }
  const smb = arSeries(mkRng('smb'), LAB_N, 0.1, 0.011)
  const hml = arSeries(mkRng('hml'), LAB_N, 0.1, 0.011)
  const fedRate: number[] = []
  const fedShock: number[] = []
  const jobs: number[] = []
  {
    const rng = mkRng('fed')
    for (let t = 0; t < LAB_N; t++) {
      fedRate.push(rng() < 0.12 ? (rng() < 0.6 ? 0.25 : -0.25) : 0)
      fedShock.push(rng() < 0.16 ? gaussian(rng) * 1.2 : 0)
      jobs.push(t % 4 === 1 ? gaussian(rng) : 0)
    }
  }
  const usdFx = arSeries(mkRng('usd'), LAB_N, 0.25, 0.008)

  // Size percentile within sector, for planted SMB loadings.
  const sizePct = new Map<string, number>()
  for (const sector of SECTORS) {
    const group = UNIVERSE.filter((e) => e.sector === sector).sort((a, b) => a.marketCap - b.marketCap)
    group.forEach((e, i) => sizePct.set(e.symbol, group.length < 2 ? 0.5 : i / (group.length - 1)))
  }

  const markers = new Map<string, number[][]>()
  const returns = new Map<string, number[]>()

  for (const e of UNIVERSE) {
    const rng = mulberry32(hashSeed(`panel:${e.symbol}`))
    const ivDiv = arSeries(rng, LAB_N, 0.85, 1)
    const skew = ivDiv.map((v) => 0.5 * v + gaussian(rng) * 0.8)
    const earnOffset = Math.floor(seeded(e.symbol, 30) * 13)
    const volume: number[] = []
    const mentions: number[] = []
    const sentiment = arSeries(rng, LAB_N, 0.6, 1)
    const newsTone = sentiment.map((v) => 0.55 * v + gaussian(rng) * 0.75)
    const earnSurprise: number[] = []
    let mPrev = 0
    for (let t = 0; t < LAB_N; t++) {
      const isEarningsWeek = t % 13 === earnOffset
      volume.push(1 + gaussian(rng) * 0.35 + (isEarningsWeek ? 2 : 0))
      mPrev = 0.7 * mPrev + gaussian(rng) + (rng() < 0.06 ? 3 : 0)
      mentions.push(mPrev)
      earnSurprise.push(isEarningsWeek ? gaussian(rng) : 0)
    }

    // Column order MUST match MARKERS in types/research.ts.
    const raw = [ivDiv, skew, volume, mentions, sentiment, newsTone, earnSurprise, fedRate, fedShock, jobs, usdFx, smb, hml]
    const z = raw.map(standardize)
    markers.set(e.symbol, z)

    // Weekly returns with planted factor + lagged-marker structure.
    const beta = 0.7 + seeded(e.symbol, 31) * 0.7
    const pct = sizePct.get(e.symbol) ?? 0.5
    const smbLoad = 1.0 - 1.2 * pct // small caps positive, mega caps negative
    const hmlLoad = (seeded(e.symbol, 32) - 0.4) * 0.8 * sectorWeightTilt(e.sector, 'ff-hml')
    const idio = (e.baseVol / 100 / Math.sqrt(52)) * 0.8
    const ret = new Array<number>(LAB_N).fill(0)
    for (let t = 1; t < LAB_N; t++) {
      let r = beta * mkt[t] + smbLoad * smb[t] + hmlLoad * hml[t] + gaussian(rng) * idio
      for (let k = 0; k < MARKERS.length; k++) {
        const id = MARKERS[k].id
        let w = PLANTED_WEEKLY_WEIGHTS[id] * sectorWeightTilt(e.sector, id)
        if (id === 'ff-smb') w *= smbLoad
        r += w * z[k][t - 1]
      }
      ret[t] = r
    }
    returns.set(e.symbol, ret)
  }

  _labPanel = { markers, returns, factors: { mkt, smb, hml } }
  return _labPanel
}

function sizeBucketFor(marketCapB: number): SizeBucket {
  if (marketCapB >= 1000) return 'Mega'
  if (marketCapB >= 200) return 'Large'
  if (marketCapB >= 50) return 'Mid'
  return 'Small'
}

function interpretComponent(topLoadings: { marker: string; loading: number }[]): string {
  const groupOf = new Map(MARKERS.map((m) => [m.id, m.group]))
  const tally = new Map<MarkerGroup, number>()
  for (const l of topLoadings) {
    const g = groupOf.get(l.marker)
    if (g) tally.set(g, (tally.get(g) ?? 0) + Math.abs(l.loading))
  }
  const ranked = [...tally.entries()].sort((a, b) => b[1] - a[1])
  const names: Record<MarkerGroup, string> = {
    macro: 'Macro / rates regime',
    sentiment: 'Attention & sentiment',
    options: 'Options positioning',
    factor: 'Style rotation (size/value)',
    fundamental: 'Earnings events',
  }
  if (ranked.length === 0) return 'Mixed factor'
  if (ranked.length > 1 && ranked[1][1] > 0.8 * ranked[0][1]) {
    return `Mixed ${names[ranked[0][0]].toLowerCase()} / ${names[ranked[1][0]].toLowerCase()} factor`
  }
  return `${names[ranked[0][0]]} factor`
}

function runMockMarkerLab(params: MarkerLabParams): MarkerLabResult {
  const panel = buildLabPanel()
  const horizon = Math.min(Math.max(params.horizonWeeks ?? 4, 1), LAB_MAX_HORIZON)
  const scope = params.sector ? UNIVERSE.filter((e) => e.sector === params.sector) : UNIVERSE

  // Pool (symbol, week) observations: lagged markers vs forward h-week returns.
  const pooled: number[][] = MARKERS.map(() => [])
  const fwd: number[] = []
  for (const e of scope) {
    const z = panel.markers.get(e.symbol)!
    const ret = panel.returns.get(e.symbol)!
    for (let t = LAB_WARMUP; t < LAB_WEEKS; t++) {
      let f = 0
      for (let u = 1; u <= horizon; u++) f += ret[t + u]
      fwd.push(f)
      for (let k = 0; k < MARKERS.length; k++) pooled[k].push(z[k][t])
    }
  }

  // Influence: |corr with forward return|, as a share of total.
  const corrs = pooled.map((col) => correlation(col, fwd))
  const totalAbs = corrs.reduce((s, c) => s + Math.abs(c), 0) || 1
  const n = fwd.length
  const influences: MarkerInfluence[] = MARKERS.map((m, k): MarkerInfluence => {
    const r = corrs[k]
    return {
      marker: m.id,
      label: m.label,
      group: m.group,
      correlation: +r.toFixed(4),
      influence: +((Math.abs(r) / totalAbs) * 100).toFixed(1),
      direction: r >= 0 ? 'positive' : 'negative',
      tStat: +((r * Math.sqrt(n - 2)) / Math.sqrt(Math.max(1e-9, 1 - r * r))).toFixed(2),
    }
  }).sort((a, b) => b.influence - a.influence)

  // Unsupervised structure: PCA on the pooled marker correlation matrix.
  const { loadings, varianceExplained } = pca(pooled)
  let cum = 0
  const components: PcaComponentSummary[] = varianceExplained.slice(0, 5).map((ve, i) => {
    cum += ve
    const withLabels = loadings[i]
      .map((loading, k) => ({ marker: MARKERS[k].id, label: MARKERS[k].label, loading }))
      .sort((a, b) => Math.abs(b.loading) - Math.abs(a.loading))
      .slice(0, 4)
      .map((l) => ({ ...l, loading: +l.loading.toFixed(3) }))
    return {
      component: i + 1,
      varianceExplained: +ve.toFixed(4),
      cumulativeVariance: +cum.toFixed(4),
      topLoadings: withLabels,
      interpretation: interpretComponent(withLabels),
    }
  })

  // Fama–French loadings per sector (OLS on the factor series).
  const sectorsInScope = params.sector ? [params.sector] : [...SECTORS]
  const sectorFactors: SectorFactorLoadings[] = []
  for (const sector of sectorsInScope) {
    const names = UNIVERSE.filter((e) => e.sector === sector)
    if (names.length === 0) continue
    const avgRet: number[] = []
    const mktS: number[] = []
    const smbS: number[] = []
    const hmlS: number[] = []
    for (let t = LAB_WARMUP; t < LAB_WEEKS; t++) {
      avgRet.push(mean(names.map((e) => panel.returns.get(e.symbol)![t])))
      mktS.push(panel.factors.mkt[t])
      smbS.push(panel.factors.smb[t])
      hmlS.push(panel.factors.hml[t])
    }
    const ols = olsRegression(avgRet, [mktS, smbS, hmlS])
    sectorFactors.push({
      sector,
      alphaAnnualPct: +(ols.intercept * 52 * 100).toFixed(2),
      marketBeta: +ols.coefficients[0].toFixed(2),
      smb: +ols.coefficients[1].toFixed(2),
      hml: +ols.coefficients[2].toFixed(2),
      r2: +ols.r2.toFixed(2),
    })
  }

  // Size placement within sector.
  const sizePlacements: SizePlacement[] = []
  for (const sector of sectorsInScope) {
    const group = UNIVERSE.filter((e) => e.sector === sector).sort((a, b) => b.marketCap - a.marketCap)
    group.forEach((e, i) => {
      sizePlacements.push({
        symbol: e.symbol,
        name: e.name,
        sector,
        marketCap: e.marketCap,
        rankInSector: i + 1,
        sectorCount: group.length,
        percentile: Math.round(group.length < 2 ? 50 : ((group.length - 1 - i) / (group.length - 1)) * 100),
        bucket: sizeBucketFor(e.marketCap),
      })
    })
  }

  // Composite signal: correlation-weighted current marker readings.
  const symbolSignals: SymbolSignal[] = scope
    .map((e) => {
      const z = panel.markers.get(e.symbol)!
      let signal = 0
      for (let k = 0; k < MARKERS.length; k++) signal += corrs[k] * z[k][LAB_WEEKS - 1]
      const expected = Math.max(-28, Math.min(32, signal * 12))
      return {
        symbol: e.symbol,
        name: e.name,
        sector: e.sector,
        signalZ: +signal.toFixed(3),
        expectedAnnualReturnPct: +expected.toFixed(1),
        annualVolPct: e.baseVol,
      }
    })
    .sort((a, b) => b.signalZ - a.signalZ)

  return {
    asOf: new Date().toISOString(),
    scope: params.sector ?? 'All sectors',
    sampleWeeks: LAB_WEEKS - LAB_WARMUP,
    symbolsAnalyzed: scope.length,
    observations: n,
    horizonWeeks: horizon,
    influences,
    components,
    sectorFactors,
    sizePlacements,
    symbolSignals,
  }
}

export async function analyzeMarkerInfluence(params: MarkerLabParams): Promise<MarkerLabResult> {
  if (USE_MOCK) {
    _usingMockData = true
    await delay(600)
    return runMockMarkerLab(params)
  }
  try {
    const result = await apiPost<MarkerLabResult>('/api/finance/research/marker-influence', params)
    _usingMockData = false
    return result
  } catch {
    console.warn('Marker-influence API failed, falling back to mock panel')
    _usingMockData = true
    return runMockMarkerLab(params)
  }
}

// ─── Convertible / preferred parity scanner ──────────────────
// Prices each instrument's embedded conversion option with Black–Scholes at
// the listed chain's (tenor-matched) IV, then solves for the vol the convert's
// market price actually pays. Positive vol gap = the embedded option is
// cheaper than the same optionality bought on the listed chain.

const RISK_FREE_PCT = 4.3

/** Continuous-annuity straight-bond value, % of par. */
function bondFloorPct(couponPct: number, tenorYears: number, yieldDecimal: number): number {
  const y = Math.max(yieldDecimal, 1e-6)
  const c = couponPct / 100
  const floor = Math.exp(-y * tenorYears) + (c * (1 - Math.exp(-y * tenorYears))) / y
  return floor * 100
}

function buildConvertibleIssue(e: UniverseEntry): ConvertibleIssue {
  const isNote = !!e.hasConvert
  const profile = mockProfile(e)
  const r = RISK_FREE_PCT / 100
  const divYield = e.baseVol > 40 ? 0 : (seeded(e.symbol, 21) * 2.5) / 100

  // Terms — the note coupon/premium formulas match mockOfferingTerm so the
  // Filings/Opus term sheets and this scanner agree.
  const par = isNote ? 1000 : 100
  const couponPct = isNote ? +(0.25 + seeded(e.symbol, 11) * 3).toFixed(2) : +(4.5 + seeded(e.symbol, 13) * 3.5).toFixed(2)
  const premiumPct = isNote ? 27.5 + seeded(e.symbol, 12) * 20 : 30 + seeded(e.symbol, 23) * 25
  const conversionPrice = +(e.price * (1 + premiumPct / 100)).toFixed(2)
  const conversionRatio = +(par / conversionPrice).toFixed(4)
  const tenorYears = +(isNote ? 1.5 + seeded(e.symbol, 20) * 3.5 : 3 + seeded(e.symbol, 24) * 2).toFixed(2)
  const maturity = isNote ? isoDaysFromNow(Math.round(tenorYears * 365)) : null
  const callYears = tenorYears * (0.35 + seeded(e.symbol, 25) * 0.3)
  const callDate = isoDaysFromNow(Math.round(callYears * 365))
  const callPricePct = isNote ? 100 : 100
  const softCallTriggerPct = isNote ? 130 : null
  const creditSpreadBps = Math.round(120 + e.baseVol * 4 + seeded(e.symbol, 26) * 120)
  const y = (RISK_FREE_PCT + creditSpreadBps / 100) / 100

  // Straight value. Perpetual preferred: dividend perpetuity, clamped.
  const floorPct = isNote
    ? +bondFloorPct(couponPct, tenorYears, y).toFixed(2)
    : +Math.min(118, Math.max(45, (couponPct / 100 / y) * 100)).toFixed(2)

  // Listed chain IV interpolated to the conversion tenor: long-dated IV
  // compresses toward the sector-typical level.
  const listedIv = +(0.6 * e.baseVol + 0.4 * profile.impliedVol30d + (seeded(e.symbol, 27) - 0.5) * 4).toFixed(1)

  // Fair value at the listed chain's vol.
  const optPerShare = bsPrice('call', e.price, conversionPrice, tenorYears, listedIv / 100, r, divYield)
  const modelValuePct = +(floorPct + ((conversionRatio * optPerShare) / par) * 100).toFixed(2)

  // Seeded mispricing tilted slightly cheap (converts usually trade below model).
  const mis = (seeded(e.symbol, 22) - 0.62) * 0.09
  let marketPricePct = modelValuePct * (1 + mis)
  marketPricePct = Math.max(marketPricePct, floorPct * 1.002 + 0.15 * (modelValuePct - floorPct))
  marketPricePct = +marketPricePct.toFixed(2)

  // Solve the vol the market price implies for the embedded option.
  const optTarget = ((marketPricePct - floorPct) / 100) * (par / conversionRatio)
  const solved = impliedVol('call', optTarget, e.price, conversionPrice, tenorYears, r, divYield)
  const convertImpliedVol = solved == null ? null : +(solved * 100).toFixed(1)
  const volGapPts = convertImpliedVol == null ? null : +(listedIv - convertImpliedVol).toFixed(1)

  const parityPct = +(((conversionRatio * e.price) / par) * 100).toFixed(2)
  const cheapnessPct = +(((modelValuePct - marketPricePct) / marketPricePct) * 100).toFixed(2)

  return {
    id: `${e.symbol}-${isNote ? 'cn' : 'cp'}`,
    symbol: e.symbol,
    company: e.name,
    sector: e.sector,
    kind: isNote ? 'Convertible Note' : 'Convertible Preferred',
    couponPct,
    parValue: par,
    maturity,
    tenorYears,
    conversionPrice,
    conversionRatio,
    stockPrice: e.price,
    callDate,
    callPricePct,
    softCallTriggerPct,
    creditSpreadBps,
    marketPricePct,
    parityPct,
    conversionPremiumPct: +(((marketPricePct - parityPct) / parityPct) * 100).toFixed(1),
    bondFloorPct: floorPct,
    embeddedOptionPct: +(marketPricePct - floorPct).toFixed(2),
    modelValuePct,
    convertImpliedVol,
    listedIv,
    volGapPts,
    cheapnessPct,
    verdict: cheapnessPct >= 2 ? 'undervalued' : cheapnessPct <= -2 ? 'rich' : 'fair',
    sourceFiling: mockFilings(e).find((f) => f.formType === '424B5' || f.formType === 'S-3') ?? null,
  }
}

function runMockConvertibleScan(params: ConvertibleScanParams): ConvertibleScanResult {
  let pool = UNIVERSE.filter((e) => e.hasConvert || e.hasPreferred)
  if (params.sector) pool = pool.filter((e) => e.sector === params.sector)
  if (params.kind === 'note') pool = pool.filter((e) => e.hasConvert)
  if (params.kind === 'preferred') pool = pool.filter((e) => e.hasPreferred)
  const issues = pool.map(buildConvertibleIssue).sort((a, b) => b.cheapnessPct - a.cheapnessPct)
  return { asOf: new Date().toISOString(), riskFreeRatePct: RISK_FREE_PCT, issues }
}

export async function scanConvertibles(params: ConvertibleScanParams): Promise<ConvertibleScanResult> {
  if (USE_MOCK) {
    _usingMockData = true
    await delay(550)
    return runMockConvertibleScan(params)
  }
  try {
    const qs = new URLSearchParams()
    if (params.sector) qs.set('sector', params.sector)
    if (params.kind && params.kind !== 'all') qs.set('kind', params.kind)
    const result = await apiGet<ConvertibleScanResult>(`/api/finance/research/convertibles?${qs}`)
    _usingMockData = false
    return result
  } catch {
    console.warn('Convertibles API failed, falling back to mock scan')
    _usingMockData = true
    return runMockConvertibleScan(params)
  }
}

// ─── Monte Carlo strategy lab ────────────────────────────────
// The simulation always runs locally (seeded, reproducible); only the
// strategy inputs (marker signals / convert scans) go through the data layer.

const MC_BINS = 36

function mcStatsFrom(pnls: number[], horizonDays: number) {
  const d = distributionStats(pnls)
  return {
    meanPnl: d.mean,
    medianPnl: d.median,
    stdevPnl: d.stdev,
    probProfit: d.probPositive,
    p5: d.p5,
    p95: d.p95,
    var95: d.var95,
    cvar95: d.cvar95,
    best: d.best,
    worst: d.worst,
    sharpe: d.stdev === 0 ? 0 : (d.mean / d.stdev) * Math.sqrt(252 / horizonDays),
  }
}

function normalOverlayFor(pnls: number[], bins: { start: number; end: number; count: number }[]) {
  if (bins.length === 0) return []
  const m = mean(pnls)
  let sd = 0
  for (const p of pnls) sd += (p - m) * (p - m)
  sd = Math.sqrt(sd / Math.max(1, pnls.length - 1)) || 1
  const width = bins[0].end - bins[0].start
  const lo = bins[0].start
  const hi = bins[bins.length - 1].end
  const pts: { x: number; y: number }[] = []
  for (let i = 0; i <= 72; i++) {
    const x = lo + ((hi - lo) * i) / 72
    pts.push({ x, y: ((pnls.length * width) / sd) * normalPdf((x - m) / sd) })
  }
  return pts
}

interface TiltName {
  weight: number
  mu: number
  sigma: number
}

function markerTiltPnls(
  names: TiltName[],
  rho: number,
  horizonYears: number,
  notional: number,
  paths: number,
  seed: number
): number[] {
  const rand = mulberry32(seed)
  const sqRho = Math.sqrt(Math.max(0, Math.min(0.99, rho)))
  const sqIdio = Math.sqrt(1 - Math.max(0, Math.min(0.99, rho)))
  const sqT = Math.sqrt(horizonYears)
  const pnls = new Array<number>(paths)
  for (let p = 0; p < paths; p++) {
    const zf = gaussian(rand)
    let ret = 0
    for (const nm of names) {
      const z = sqRho * zf + sqIdio * gaussian(rand)
      ret += nm.weight * (Math.exp((nm.mu - 0.5 * nm.sigma * nm.sigma) * horizonYears + nm.sigma * sqT * z) - 1)
    }
    pnls[p] = notional * ret
  }
  return pnls
}

function gbmSamplePaths(
  mu: number,
  sigma: number,
  horizonDays: number,
  notional: number,
  count: number,
  seed: number
): number[][] {
  const rand = mulberry32(seed ^ 0x9e3779b9)
  const dt = 1 / 252
  const paths: number[][] = []
  for (let i = 0; i < count; i++) {
    let v = notional
    const path = [v]
    for (let d = 0; d < horizonDays; d++) {
      v *= Math.exp((mu - 0.5 * sigma * sigma) * dt + sigma * Math.sqrt(dt) * gaussian(rand))
      path.push(v)
    }
    paths.push(path)
  }
  return paths
}

async function simulateMarkerTilt(cfg: McConfig, basisMock: boolean): Promise<McResult> {
  const lab = await analyzeMarkerInfluence({ sector: cfg.sector ?? null, horizonWeeks: 4 })
  const picks = lab.symbolSignals.slice(0, 8)
  const rawW = picks.map((s) => Math.max(s.signalZ, 0) + 0.15)
  const totalW = rawW.reduce((a, b) => a + b, 0)
  const names: TiltName[] = picks.map((s, i) => ({
    weight: rawW[i] / totalW,
    mu: s.expectedAnnualReturnPct / 100 + cfg.driftAdjustPct / 100,
    sigma: (s.annualVolPct / 100) * cfg.volMultiplier,
  }))
  const rho = 0.45
  const h = cfg.horizonDays / 252

  const pnls = markerTiltPnls(names, rho, h, cfg.notional, cfg.paths, cfg.seed)
  const bins = histogram(pnls, MC_BINS)

  // Portfolio-level GBM for the display paths.
  const muP = names.reduce((s, nm) => s + nm.weight * nm.mu, 0)
  let varP = 0
  for (const a of names) for (const b of names) varP += a.weight * b.weight * a.sigma * b.sigma * (a === b ? 1 : rho)
  const samplePaths = gbmSamplePaths(muP, Math.sqrt(varP), cfg.horizonDays, cfg.notional, 30, cfg.seed)

  // Sensitivity: ±20% on each input, same seed, capped path count.
  const sensPaths = Math.min(cfg.paths, 2500)
  const sens: McSensitivity[] = []
  const variants: { param: string; label: string; run: (m: number) => number[] }[] = [
    {
      param: 'drift',
      label: 'Expected returns (drift)',
      run: (m) => markerTiltPnls(names.map((nm) => ({ ...nm, mu: nm.mu * m })), rho, h, cfg.notional, sensPaths, cfg.seed),
    },
    {
      param: 'vol',
      label: 'Volatility',
      run: (m) => markerTiltPnls(names.map((nm) => ({ ...nm, sigma: nm.sigma * m })), rho, h, cfg.notional, sensPaths, cfg.seed),
    },
    {
      param: 'correlation',
      label: 'Cross-name correlation',
      run: (m) => markerTiltPnls(names, rho * m, h, cfg.notional, sensPaths, cfg.seed),
    },
    {
      param: 'horizon',
      label: 'Holding horizon',
      run: (m) => markerTiltPnls(names, rho, h * m, cfg.notional, sensPaths, cfg.seed),
    },
  ]
  for (const v of variants) {
    await delay(15)
    sens.push({ param: v.param, label: v.label, low: mean(v.run(0.8)), high: mean(v.run(1.2)) })
  }
  sens.sort((a, b) => Math.abs(b.high - b.low) - Math.abs(a.high - a.low))

  const positions: McPosition[] = picks.map((s, i) => ({
    symbol: s.symbol,
    weightPct: +((rawW[i] / totalW) * 100).toFixed(1),
    note: `signal ${s.signalZ >= 0 ? '+' : ''}${s.signalZ}σ · E[r] ${s.expectedAnnualReturnPct}%/yr · vol ${s.annualVolPct}%`,
  }))

  return {
    config: cfg,
    strategyLabel: 'Marker-tilt long portfolio (top signals, influence-weighted)',
    positions,
    stats: mcStatsFrom(pnls, cfg.horizonDays),
    histogram: bins,
    normalOverlay: normalOverlayFor(pnls, bins),
    samplePaths,
    sensitivities: sens,
    basisMock,
  }
}

interface ParityLeg {
  issue: ConvertibleIssue
  units: number
  deltaShares: number
  value0: number
}

function parityPnls(
  legs: ParityLeg[],
  opts: { kappa: number; volMult: number; spreadMult: number; drift: number },
  horizonYears: number,
  paths: number,
  seed: number
): number[] {
  const rand = mulberry32(seed)
  const r = RISK_FREE_PCT / 100
  const pnls = new Array<number>(paths)
  const sqH = Math.sqrt(Math.max(horizonYears, 1e-6))
  for (let p = 0; p < paths; p++) {
    // Systematic credit-spread shock shared by every leg on this path.
    const spreadShock = Math.exp(0.28 * gaussian(rand) * sqH * 2)
    let pnl = 0
    for (const leg of legs) {
      const iss = leg.issue
      const sigmaS = (iss.listedIv / 100) * opts.volMult
      const sT = iss.stockPrice * Math.exp((opts.drift - 0.5 * sigmaS * sigmaS) * horizonYears + sigmaS * sqH * gaussian(rand))
      const convIv0 = iss.convertImpliedVol ?? iss.listedIv
      // Vol gap converges at rate kappa but is remarked with real noise.
      const ivT = Math.max(5, iss.listedIv + (convIv0 - iss.listedIv) * Math.exp(-opts.kappa * horizonYears) + gaussian(rand) * 2.5)
      const yStressed = (RISK_FREE_PCT + (iss.creditSpreadBps * opts.spreadMult * spreadShock) / 100) / 100
      const remaining = Math.max(iss.tenorYears - horizonYears, 1 / 52)
      const floorT = iss.maturity
        ? Math.min(iss.callPricePct + 6, bondFloorPct(iss.couponPct, remaining, yStressed))
        : Math.min(118, Math.max(45, (iss.couponPct / 100 / yStressed) * 100))
      const optT = bsPrice('call', sT, iss.conversionPrice, remaining, ivT / 100, r, 0)
      // Residual basis risk: converts do not mark exactly at model.
      const basisNoisePct = gaussian(rand) * 0.75
      const valueT = ((floorT + ((iss.conversionRatio * optT) / iss.parValue) * 100 + basisNoisePct) / 100) * iss.parValue
      const coupon = ((iss.couponPct / 100) * iss.parValue * horizonYears)
      pnl += leg.units * (valueT - leg.value0 + coupon) - leg.deltaShares * (sT - iss.stockPrice)
    }
    pnls[p] = pnl
  }
  return pnls
}

async function simulateConvertParity(cfg: McConfig, basisMock: boolean): Promise<McResult> {
  const scan = await scanConvertibles({ sector: cfg.sector ?? null, kind: 'all' })
  let picks = scan.issues.filter((i) => i.verdict === 'undervalued').slice(0, 5)
  let note = 'undervalued vs listed-chain parity'
  if (picks.length === 0) {
    picks = scan.issues.slice(0, 3)
    note = 'cheapest available (none strictly undervalued)'
  }
  const r = RISK_FREE_PCT / 100
  const alloc = cfg.notional / picks.length
  const legs: ParityLeg[] = picks.map((iss) => {
    const value0 = (iss.marketPricePct / 100) * iss.parValue
    const units = alloc / value0
    const delta = bsDelta('call', iss.stockPrice, iss.conversionPrice, iss.tenorYears, iss.listedIv / 100, r, 0)
    return { issue: iss, units, deltaShares: delta * iss.conversionRatio * units, value0 }
  })

  const h = cfg.horizonDays / 252
  const base = { kappa: 3.0, volMult: cfg.volMultiplier, spreadMult: 1, drift: cfg.driftAdjustPct / 100 }
  const pnls = parityPnls(legs, base, h, cfg.paths, cfg.seed)
  const bins = histogram(pnls, MC_BINS)

  // Display paths: hedged book ≈ low-vol drift toward the mean convergence P&L.
  const meanPnl = mean(pnls)
  let sd = 0
  for (const p of pnls) sd += (p - meanPnl) * (p - meanPnl)
  sd = Math.sqrt(sd / Math.max(1, pnls.length - 1))
  const muPath = Math.log(1 + meanPnl / cfg.notional) / Math.max(h, 1e-6)
  const sigPath = sd / cfg.notional / Math.sqrt(Math.max(h, 1e-6))
  const samplePaths = gbmSamplePaths(muPath, sigPath, cfg.horizonDays, cfg.notional, 30, cfg.seed)

  const sensPaths = Math.min(cfg.paths, 2500)
  const sens: McSensitivity[] = []
  const variants: { param: string; label: string; run: (m: number) => number[] }[] = [
    { param: 'kappa', label: 'Vol-gap convergence speed', run: (m) => parityPnls(legs, { ...base, kappa: base.kappa * m }, h, sensPaths, cfg.seed) },
    { param: 'vol', label: 'Stock volatility', run: (m) => parityPnls(legs, { ...base, volMult: base.volMult * m }, h, sensPaths, cfg.seed) },
    { param: 'spread', label: 'Credit spreads', run: (m) => parityPnls(legs, { ...base, spreadMult: m }, h, sensPaths, cfg.seed) },
    { param: 'horizon', label: 'Holding horizon', run: (m) => parityPnls(legs, base, h * m, sensPaths, cfg.seed) },
  ]
  for (const v of variants) {
    await delay(15)
    sens.push({ param: v.param, label: v.label, low: mean(v.run(0.8)), high: mean(v.run(1.2)) })
  }
  sens.sort((a, b) => Math.abs(b.high - b.low) - Math.abs(a.high - a.low))

  const positions: McPosition[] = picks.map((iss, i) => ({
    symbol: iss.symbol,
    weightPct: +((100 / picks.length)).toFixed(1),
    note: `${iss.kind} · ${iss.cheapnessPct}% cheap · vol gap ${iss.volGapPts ?? '—'} pts · ${i === 0 ? note : 'delta-hedged'}`,
  }))

  return {
    config: cfg,
    strategyLabel: 'Convertible parity capture (long convert, delta-hedged short stock)',
    positions,
    stats: mcStatsFrom(pnls, cfg.horizonDays),
    histogram: bins,
    normalOverlay: normalOverlayFor(pnls, bins),
    samplePaths,
    sensitivities: sens,
    basisMock,
  }
}

export async function runStrategyMonteCarlo(cfg: McConfig): Promise<McResult> {
  const clamped: McConfig = {
    ...cfg,
    paths: Math.max(500, Math.min(20000, Math.round(cfg.paths))),
    horizonDays: Math.max(5, Math.min(252, Math.round(cfg.horizonDays))),
    volMultiplier: Math.max(0.25, Math.min(3, cfg.volMultiplier)),
  }
  if (clamped.strategy === 'marker-tilt') {
    const result = await simulateMarkerTilt(clamped, false)
    return { ...result, basisMock: isResearchUsingMockData() }
  }
  const result = await simulateConvertParity(clamped, false)
  return { ...result, basisMock: isResearchUsingMockData() }
}
